import React, { useState, useEffect, useCallback } from "react";
import {
  Card, CircularProgress, Chip,
  MenuItem, Select, FormControl, InputLabel,
  TextField, IconButton,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import RefreshIcon from "@mui/icons-material/Refresh";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DataTable from "examples/Tables/DataTable";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import axios from "axios";
import { Notify } from "notiflix";
import { ENDPOINT } from "constants";
import { useSelector } from "react-redux";
import InquiryDetail from "./InquiryDetail";

const STATUS_COLOR = {
  pending:  "warning",
  read:     "info",
  replied:  "success",
  archived: "default",
};

const ContactInquiryList = () => {
  const { user } = useSelector((state) => state.auth);
  const token = user?.accessToken;

  const [inquiries, setInquiries]     = useState([]);
  const [loading, setLoading]         = useState(false);
  const [selected, setSelected]       = useState(null);
  const [search, setSearch]           = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [pagination, setPagination]   = useState({ total: 0, page: 1, totalPages: 1 });

  const fetchInquiries = useCallback(async (page = pagination.page) => {
    try {
      setLoading(true);
      const params = { page, limit: 20 };
      if (statusFilter) params.status = statusFilter;

      const { data } = await axios.get(`${ENDPOINT}api/v1/contact/inquiries`, {
        params,
        headers: { Authorization: `Bearer ${token}` },
      });

      if (data?.success || data?.statusCode === 200) {
        setInquiries(data.data?.inquiries || []);
        setPagination(data.data?.pagination || { total: 0, page: 1, totalPages: 1 });
      } else {
        Notify.failure(data?.message || "Failed to fetch inquiries");
      }
    } catch (err) {
      Notify.failure(err?.response?.data?.message || "Error fetching inquiries");
    } finally {
      setLoading(false);
    }
  }, [token, statusFilter, pagination.page]);

  useEffect(() => { fetchInquiries(); }, [fetchInquiries]);

  const filtered = search.trim()
    ? inquiries.filter((i) =>
        i.email?.toLowerCase().includes(search.toLowerCase()) ||
        i.message?.toLowerCase().includes(search.toLowerCase()) ||
        i.source?.toLowerCase().includes(search.toLowerCase())
      )
    : inquiries;

  const columns = [
    { Header: "Email",   accessor: "email",   width: "22%" },
    { Header: "Source",  accessor: "source",  width: "20%" },
    { Header: "Message", accessor: "message", width: "38%" },
    { Header: "Status",  accessor: "status",  align: "center", width: "10%" },
    { Header: "Actions", accessor: "actions", align: "center", width: "10%" },
  ];

  const rows = filtered.map((inq) => ({
    email: (
      <MDTypography variant="caption" color="text" fontWeight="medium">
        {inq.email || "—"}
      </MDTypography>
    ),
    source: (
      <MDTypography variant="caption" color="text" sx={{ fontFamily: "monospace" }}>
        {inq.source || "—"}
      </MDTypography>
    ),
    message: (
      <MDTypography
        variant="caption" color="text"
        sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", display: "block", maxWidth: 340 }}
      >
        {inq.message || "—"}
      </MDTypography>
    ),
    status: (
      <Chip
        label={inq.status || "pending"}
        color={STATUS_COLOR[inq.status] || "default"}
        size="small"
        sx={{ fontWeight: 600, fontSize: 11, textTransform: "capitalize" }}
      />
    ),
    actions: (
      <MDButton size="small" variant="outlined" color="info" onClick={() => setSelected(inq)}>
        View
      </MDButton>
    ),
  }));

  if (selected) {
    return (
      <InquiryDetail
        inquiry={selected}
        onBack={() => { setSelected(null); fetchInquiries(); }}
        onUpdated={fetchInquiries}
      />
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Card>
          {/* Header */}
          <MDBox
            mx={2} mt={-3} py={3} px={2}
            variant="gradient" bgColor="info"
            borderRadius="lg" coloredShadow="info"
            display="flex" justifyContent="space-between" alignItems="center"
          >
            <MDTypography variant="h6" color="white">
              Contact Inquiries
              {pagination.total > 0 && (
                <MDTypography component="span" variant="caption" color="white" sx={{ ml: 1, opacity: 0.8 }}>
                  ({pagination.total} total)
                </MDTypography>
              )}
            </MDTypography>
            <IconButton size="small" onClick={() => fetchInquiries()} sx={{ color: "white" }}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </MDBox>

          {/* Filters */}
          <MDBox px={3} pt={3} pb={1} display="flex" gap={2} flexWrap="wrap" alignItems="center">
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                onChange={(e) => { setStatusFilter(e.target.value); setPagination((p) => ({ ...p, page: 1 })); }}
                label="Status"
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="read">Read</MenuItem>
                <MenuItem value="replied">Replied</MenuItem>
                <MenuItem value="archived">Archived</MenuItem>
              </Select>
            </FormControl>

            <TextField
              size="small"
              placeholder="Search email, source, message…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              sx={{ minWidth: 260 }}
              InputProps={{
                endAdornment: (
                  <IconButton size="small" disableRipple>
                    <SearchIcon fontSize="small" />
                  </IconButton>
                ),
              }}
            />
          </MDBox>

          {/* Table */}
          <MDBox px={2} pb={2}>
            {loading ? (
              <MDBox display="flex" justifyContent="center" py={6}>
                <CircularProgress />
              </MDBox>
            ) : filtered.length === 0 ? (
              <MDBox py={6} textAlign="center">
                <MDTypography variant="body2" color="secondary">
                  No inquiries found.
                </MDTypography>
              </MDBox>
            ) : (
              <DataTable
                table={{ columns, rows }}
                isSorted={false}
                entriesPerPage={false}
                showTotalEntries={false}
                canSearch={false}
                noEndBorder
              />
            )}
          </MDBox>

          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <MDBox px={3} pb={3} display="flex" justifyContent="flex-end" alignItems="center" gap={1}>
              <MDButton
                size="small" variant="outlined" color="info"
                disabled={pagination.page <= 1}
                onClick={() => setPagination((p) => ({ ...p, page: p.page - 1 }))}
              >
                Prev
              </MDButton>
              <MDTypography variant="caption" color="text">
                Page {pagination.page} of {pagination.totalPages}
              </MDTypography>
              <MDButton
                size="small" variant="outlined" color="info"
                disabled={pagination.page >= pagination.totalPages}
                onClick={() => setPagination((p) => ({ ...p, page: p.page + 1 }))}
              >
                Next
              </MDButton>
            </MDBox>
          )}
        </Card>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
};

export default ContactInquiryList;
